import { LabsExperimentExposure } from "@/features/labs/labExperiment";
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

import { cartUi, useCartOpen } from '@/features/marketplace/stores/cartUi';
import { useHydrateSuggestedCart } from '@/features/marketplace/useHydrateSuggestedCart';
import { setExperimentPersonProperties } from '@/lib/analytics';
import {
  useBootstrap,
  useNativePatientRequests,
  useMyExperimentAssignments,
  useRequiredIntakes,
} from '@/lib/api/hooks';
import {
  shouldAutoStartTour,
  useTourFlagsVersion,
  useTourRunning,
} from '@/lib/tourFlags';
import {
  ExperimentsContext,
  readExperimentAssignments,
  writeExperimentAssignments,
} from '@/lib/experiments';
import { Button } from '@/design-system/primitives';
import { ArrowInline } from '@/lib/Icon';

import { MessagesHub } from '@/features/messaging/MessagesHub';
import { messagingHub } from '@/features/messaging/messagingHub';
import { nativeSupportEnabled } from '@/features/native-support/types';
import { requestedIdFromSearch, requestSelectionFromSearch } from '@/features/native-support/requestFeed';
import { PostPurchaseUpsellMount } from '@/features/upsell/PostPurchaseUpsellMount';
import { GuidedTour } from '@/features/tour/GuidedTour';
import { WelcomeVideo } from '@/features/onboarding/WelcomeVideo';
import { markWelcomeVideoDone } from '@/lib/welcomeVideoFlags';
import { useWelcomeVideoGate } from '@/features/onboarding/useWelcomeVideoGate';

import { Sidebar } from './Sidebar';
import { SearchBar } from './SearchBar';
import { MobileBottomNav } from './MobileBottomNav';
import { OverflowDrawer } from './OverflowDrawer';
import { IntakeAttentionDrawer } from './IntakeAttentionDrawer';
import { CartDrawer } from './CartDrawer';
import { ReferralBanner } from './ReferralBanner';
import { TimezoneSyncToast } from './TimezoneSyncToast';
import { IdentityStatusBanner } from '@/features/onboarding/IdentityStatusBanner';

/**
 * AppLayout — new shell.
 *
 * Mobile: full-bleed SearchBar at top, content scrolls in the middle,
 * fixed MobileBottomNav at bottom with Stack / Orders / Learn / ⋯. The
 * ⋯ opens an OverflowDrawer with the rest of the routes (Home / Plan /
 * Clinic / Upgrade / Account) + Need Help? footer.
 *
 * Desktop (md+): keeps the left Sidebar (auto-visible), same SearchBar
 * at the top of the content rail, bottom nav hidden. Sidebar's mobile
 * slide-in behavior is dormant — kept around so we don't churn its
 * internals; only the new shell drives mobile UX.
 */
export function AppLayout() {
  const [overflowOpen, setOverflowOpen] = useState(false);
  const cartOpen = useCartOpen();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: requiredIntakes } = useRequiredIntakes();
  const native = nativeSupportEnabled();
  const requestedId = requestedIdFromSearch(location.search);
  const notificationFeed = useNativePatientRequests(native && requestedId !== null);
  const openedNotification = useRef<string | null>(null);
  useEffect(() => {
    if (!native || !requestedId || notificationFeed.isError) return;
    const owned = new Set((notificationFeed.data?.requests ?? []).map(request => request.id));
    const selected = requestSelectionFromSearch(location.search, native, owned);
    if (selected && openedNotification.current !== location.key) {
      openedNotification.current = location.key;
      messagingHub.open({ team: 'support', source: 'notification' });
      messagingHub.showSupportView({ kind: 'request', id: selected });
      const search = new URLSearchParams(location.search);
      search.delete('support_request');
      navigate({ pathname: location.pathname, search: search.toString(), hash: location.hash }, { replace: true, state: location.state });
    } else if (!selected && !notificationFeed.data?.requests_complete && notificationFeed.hasNextPage && !notificationFeed.isFetchingNextPage) {
      void notificationFeed.fetchNextPage();
    }
  }, [native, requestedId, location.key, location.pathname, location.search, location.hash, location.state, navigate,
    notificationFeed.data, notificationFeed.isError, notificationFeed.hasNextPage, notificationFeed.isFetchingNextPage,
    notificationFeed.fetchNextPage]);


  // Experiment cohorting: initialise from the cookie synchronously for an
  // instant first paint (e.g. inhouse-chat variant decides SupportChat vs
  // PlainChat — empty initial state would flicker). Then the backend
  // resolves the authoritative map from experiment_assignments (Phase C
  // of the persistence rollout — replaces the prior client-side dice
  // roll, see web-app/src/lib/experiments.ts for the migration note).
  //
  // Backend response also seeds + reconciles the cookie via
  // writeExperimentAssignments so synchronous readers like
  // useShouldShowPostPurchaseUpsell see the post-auth values.
  const [assignments, setAssignments] = useState<Record<string, string>>(
    readExperimentAssignments,
  );
  const { data: myAssignments } = useMyExperimentAssignments();
  useEffect(() => {
    if (!myAssignments?.assignments) return;
    setAssignments(myAssignments.assignments);
    writeExperimentAssignments(myAssignments.assignments);
    // Attribute the patient's cohorts onto the PostHog person so portal
    // events (upsell_viewed, complete_purchase, …) can be broken down by
    // variant. The assignments come from an authed endpoint, so the patient
    // is already identified — this is the portal's own attribution, not the
    // (unreliable) marketing→portal person stitching.
    setExperimentPersonProperties(myAssignments.assignments);
  }, [myAssignments]);

  // Admin-assigned "suggested cart" (patient profile builder): added to the
  // cart once, then claimed. See useHydrateSuggestedCart.
  useHydrateSuggestedCart();

  // In-house AI chat rolled out to 100% (inhouse-chat experiment completed
  // 2026-07-13, variant-a won) — SupportChat for everyone.
  // Sticky CTA shows on every patient page except Home (where the intake
  // prompt already owns the hero) and the intake wizard itself. Same
  // visible-when-pending logic as HomeScreen.PendingIntakesCard.
  const hasPendingIntake = (requiredIntakes ?? []).some(
    (i) => !i.intake_submitted_at,
  );
  const isHome =
    location.pathname === '/app' || location.pathname === '/app/';
  const isIntakeRoute = location.pathname.startsWith('/app/intake');
  const isLabRoute = location.pathname === '/app/labs' || location.pathname.startsWith('/app/labs/');
  const showIntakeCta = hasPendingIntake && !isHome && !isIntakeRoute && !isLabRoute;

  // Hold the post-purchase upsell while the guided tour is queued (fresh
  // onboarding completion landing on Home) or running — both overlays at
  // once would bury the walkthrough under a purchase pitch. The upsell
  // mounts as soon as the tour finishes; its dismiss flags are untouched.
  const { data: bootstrap } = useBootstrap();
  const welcomeForced = isHome && new URLSearchParams(location.search).get('welcome-video') === '1';
  const { pending: welcomePending, queued: welcomeQueued, show: showWelcome } = useWelcomeVideoGate({
    patientId: bootstrap?.patient.id,
    eligible: isHome && Boolean(bootstrap?.portal_access_ready ?? bootstrap?.onboarding_state === 'complete'),
    forced: welcomeForced,
  });
  const finishWelcome = () => {
    // A QA replay alone must not change the normal first-run flags.
    if (welcomePending && bootstrap) markWelcomeVideoDone(bootstrap.patient.id);
    if (welcomeForced) {
      const next = new URLSearchParams(location.search);
      next.delete('welcome-video');
      navigate({ pathname: location.pathname, search: next.toString(), hash: location.hash }, { replace: true });
    }
  };
  const tourRunning = useTourRunning();
  // Subscribe to flag writes so `tourQueued` re-reads the moment the tour
  // engine settles a control-cohort patient (guided-tour-v1 experiment) —
  // otherwise the upsell would stay held until an unrelated re-render.
  useTourFlagsVersion();
  const tourQueued = Boolean(
    bootstrap?.patient.id && shouldAutoStartTour(bootstrap.patient.id),
  );
  const holdUpsell =
    isLabRoute || tourRunning || location.search.includes('tour=1') || (isHome && (tourQueued || welcomeQueued));

  // Controlled SearchBar value so we can wipe it when the patient leaves
  // /app/search — otherwise a stale query lingers in the input across
  // every screen.
  const [searchValue, setSearchValue] = useState('');
  const isSearchRoute = location.pathname === '/app/search';
  useEffect(() => {
    if (!isSearchRoute) setSearchValue('');
  }, [isSearchRoute]);

  return (
    <ExperimentsContext.Provider value={assignments}>
      <LabsExperimentExposure />
    <div className="min-h-screen bg-[var(--surface-page)] text-ink">
      {/* Sidebar (desktop only — hidden on mobile by its own md:translate-x-0
          breakpoint). We pass open=false so the mobile-slide variant stays
          parked behind the new BottomNav + OverflowDrawer flow. */}
      <Sidebar open={false} onClose={() => undefined} />

      <div className="md:ml-[var(--sidebar-width)] min-h-screen flex flex-col">
        <SearchBar
          value={searchValue}
          onChange={setSearchValue}
          onCartClick={() => cartUi.open()}
          onSubmit={(q) => {
            const trimmed = q.trim();
            if (!trimmed) return;
            navigate(`/app/search?q=${encodeURIComponent(trimmed)}`);
          }}
        />
        {/* Bottom padding: mobile = BottomNav (h-50) + 30 breathing room.
            Desktop has no nav, so just the 30 breathing room remains. */}
        {/* Screens own their own horizontal padding (so PageHeader can
            stay full-bleed without negative-margin gymnastics). */}
        <main className="flex-1 pb-110 md:pb-30 max-w-content mx-auto w-full leading-120">
          <IdentityStatusBanner suppressed={cartOpen} />
          <Outlet />
        </main>
      </div>

      {showIntakeCta && (
        <div className="md:hidden fixed inset-x-0 bottom-50 z-helpdock pointer-events-none">
          <div className="max-w-[50rem] mx-auto px-15 pb-20 pointer-events-auto">
            <Link to="/app/intake" className="block w-full">
              <Button className="w-full flex justify-between shadow-elevated">
                <span className="blink">Continue intake</span>
                <span className="blink"><ArrowInline /></span>
              </Button>
            </Link>
          </div>
        </div>
      )}

      <MobileBottomNav onOpenOverflow={() => setOverflowOpen(true)} />
      <OverflowDrawer open={overflowOpen} onClose={() => setOverflowOpen(false)} />
      {/* Once-per-session prompt surfacing outstanding intakes on landing.
          Held back on the intake route itself and while the guided tour /
          post-purchase upsell own the screen. */}
      <IntakeAttentionDrawer suppressed={isIntakeRoute || holdUpsell} />
      <CartDrawer open={cartOpen} onClose={() => cartUi.close()} />
      <MessagesHub />
      {!holdUpsell && <PostPurchaseUpsellMount />}

      <ReferralBanner />
      <TimezoneSyncToast />
      {showWelcome && bootstrap && <WelcomeVideo key={bootstrap.patient.id} patientId={bootstrap.patient.id} preview={welcomeForced} onContinue={finishWelcome} />}
      <GuidedTour
        blocked={welcomeQueued}
        openOverflow={() => setOverflowOpen(true)}
        closeOverflow={() => setOverflowOpen(false)}
      />
    </div>
    </ExperimentsContext.Provider>
  );
}
